/**
 * Number Picker UI Component
 *
 * @example <sc-number-picker ng-model="vm.model" min="vm.min" max="vm.max"></sc-number-picker>
 * @description Component of Sandcastle Framework. Inserts a number picker UI component.
 * @namespace Core
 * @memberof TR
 */

'use strict';

namespace Core {

    class ScNumberPicker implements ng.IDirective {

        restrict: string;
        prefix: string;
        replace: boolean;
        transclude: boolean;
        require: string;
        scope: {};
        getRandomID;
        template;

        constructor() {

            this.restrict = 'E';
            this.replace = true;
            this.require = 'ngModel';
            this.scope = {
                model: '=ngModel',
                max: '=',
                min: '=',
                increment: '=',
                disabled: '@',
                onChange: '&'
            };

            this.template =  `<form class="c-number-picker">
                                <button 
                                    href="#" 
                                    ng-click="click.decrease()" 
                                    ng-disabled="model <= min || disabled === 'true'" 
                                    class="c-number-picker__part--minus"
                                    aria-controls="{{componentId}}"
                                    aria-label="Decrease">
                                </button>
                                <input 
                                    id="{{componentId}}"
                                    type="text" 
                                    class="c-number-picker__label" 
                                    ng-model="model" 
                                    aria-live="assertive"
                                    readonly/>
                                <button 
                                    href="#" 
                                    ng-click="click.increase()" 
                                    ng-disabled="model >= max || disabled === 'true'" 
                                    class="c-number-picker__part--plus"
                                    aria-controls="{{componentId}}"
                                    aria-label="Increase">
                                </button>
                              </form>`;

            /**
             * Prefix for randomised ID on each directive

             * @private
             */
            this.prefix = 'c-number-picker--';

            /**
             * Generate random ID
             *
             * @param {string} prefix BEMIT formatted identifier. e.g. 'c-component--'
             * @returns {string} Prefix + Random string
             */
            this.getRandomID = (prefix) => {
                return prefix + Math.random().toString(36).substr(2, 7);
            };
        }

        link = (scope: ng.IScope, element: ng.IAugmentedJQuery, attrs: ng.IAttributes, ngModel) => {
            const radix: number = 10;
            const increment = parseInt(scope.increment || 1, radix);
            let max: number = parseInt(scope.max, radix);
            let min: number = parseInt(scope.min, radix);

            if (scope.disabled !== 'true') {
                scope.$watch('max', (val) => {
                    max = Number(val);
                });
                scope.$watch('min', (val) => {
                    min = Number(val);
                });
            }

            //Used to apply a unique id to the element for ARIA purposes
            scope.componentId = this.getRandomID(this.prefix);

            if (scope.model === undefined) {
                ngModel.$setViewValue(0);
            }

            if (scope.disabled === 'true') {
                max = min = scope.model;
            }

            if (scope.onChange) {
                scope.$watch('model', function() {
                    scope.onChange();
                });
            }

            scope.click = {
                increase: () => {
                    if (scope.disabled === 'true') { return; }
                    if (scope.model < max) {
                        ngModel.$setViewValue(Number(scope.model) + increment);
                    }
                },
                decrease: () => {
                    if (scope.disabled === 'true') { return; }
                    if (scope.model > min) {
                        ngModel.$setViewValue(Number(scope.model) - increment);
                    }
                }
            };

        }

        static factory(): ng.IDirectiveFactory {
            const directive = () => new ScNumberPicker();
            directive.$inject = [];
            return directive;
        }

    }

    const TR = angular.module('TR');
    TR.directive('scNumberPicker', ScNumberPicker.factory());

}
